import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardSummary, getPOs, getInvoices } from '../services/api';
import { formatCurrency, formatDate, StatusBadge } from '../utils/helpers';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FileText, Receipt, TrendingUp, AlertTriangle, Clock, CheckCircle, ArrowRight } from 'lucide-react';

const COLORS = ['#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899'];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [recentPOs, setRecentPOs] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      getDashboardSummary(),
      getPOs({ limit: 5 }),
      getInvoices({ limit: 5 })
    ]).then(([s, pos, inv]) => {
      setSummary(s.data);
      setRecentPOs(pos.data.purchase_orders || []);
      setRecentInvoices(inv.data.invoices || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  const installmentStats = summary?.installments || [];
  const invoiceStats = summary?.invoices || [];
  const totalInstAmt = installmentStats.reduce((s, r) => s + parseFloat(r.total || 0), 0);
  const paidAmt = installmentStats.find(r => r.status === 'PAID')?.total || 0;
  const overdueAmt = installmentStats.find(r => r.status === 'OVERDUE')?.total || 0;
  const dueAmt = installmentStats.find(r => r.status === 'DUE')?.total || 0;
  const totalUnbilled = parseFloat(summary?.unbilled?.total || 0);

  const kpis = [
    { label: 'Total Scheduled', value: formatCurrency(totalInstAmt), icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Amount Paid', value: formatCurrency(paidAmt), icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Currently Due', value: formatCurrency(dueAmt), icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { label: 'Overdue Amount', value: formatCurrency(overdueAmt), icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Unbilled Revenue', value: formatCurrency(totalUnbilled), icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Total Invoices', value: invoiceStats.reduce((s, r) => s + parseInt(r.count), 0), icon: Receipt, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  ];

  const instChartData = installmentStats.map(r => ({ name: r.status?.replace(/_/g,' '), value: parseFloat(r.total || 0), count: parseInt(r.count) }));
  const invChartData = invoiceStats.map(r => ({ name: r.status?.replace(/_/g,' '), value: parseFloat(r.total || 0), count: parseInt(r.count) }));

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className="card p-4">
            <div className={`w-10 h-10 ${k.bg} rounded-lg flex items-center justify-center mb-3`}>
              <k.icon size={20} className={k.color} />
            </div>
            <div className="text-xl font-bold text-gray-900">{k.value}</div>
            <div className="text-xs text-gray-500 mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Installments by Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={instChartData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Invoices by Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={invChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, count }) => `${name} (${count})`} labelLine={false}>
                {invChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h3 className="font-semibold text-gray-800">Recent Sales Orders</h3>
            <button onClick={() => navigate('/purchase-orders')} className="text-blue-600 text-sm hover:underline flex items-center gap-1">View all <ArrowRight size={14} /></button>
          </div>
          <div className="divide-y divide-gray-100">
            {recentPOs.length === 0 ? <EmptyRow msg="No purchase orders yet" /> : recentPOs.map(po => (
              <div key={po.po_id} className="px-5 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/purchase-orders/${po.po_id}`)}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{po.po_number}</div>
                    <div className="text-xs text-gray-500">{po.title} · {po.buyer_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">{formatCurrency(po.po_value, po.currency)}</div>
                    <StatusBadge status={po.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h3 className="font-semibold text-gray-800">Recent Invoices</h3>
            <button onClick={() => navigate('/invoices')} className="text-blue-600 text-sm hover:underline flex items-center gap-1">View all <ArrowRight size={14} /></button>
          </div>
          <div className="divide-y divide-gray-100">
            {recentInvoices.length === 0 ? <EmptyRow msg="No invoices yet" /> : recentInvoices.map(inv => (
              <div key={inv.invoice_id} className="px-5 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{inv.invoice_number}</div>
                    <div className="text-xs text-gray-500">{inv.po_number} · Due {formatDate(inv.due_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">{formatCurrency(inv.total_amount, inv.currency)}</div>
                    <StatusBadge status={inv.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array(6).fill(0).map((_, i) => <div key={i} className="h-28 bg-gray-200 rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-72 bg-gray-200 rounded-xl" />
        <div className="h-72 bg-gray-200 rounded-xl" />
      </div>
    </div>
  );
}

function EmptyRow({ msg }) {
  return <div className="px-5 py-8 text-center text-sm text-gray-400">{msg}</div>;
}
